import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, signal, type SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconInduxtryComponent } from 'src/app/shared/components/common/icon-induxtry/icon-induxtry.component';
import { ButtonComponent } from 'src/app/shared/components/inputs/button/button.component';
import { ButtonInduxtry } from 'src/utils/models/input.interface';
import { Subscription, interval } from 'rxjs';
import { IconActionButtonComponent } from 'src/app/shared/components/common/icon-action-button/icon-action-button.component';

import { STATUS_ICONS } from '../../models/conexoes.model';
import { SourceItem } from '../../models/lakehouse.model';

export type DataSourceStatus = 'online' | 'offline';

/** @deprecated Use SourceItem from lakehouse.model */
export type DataSourceItem = SourceItem;

interface GroupedItem extends DataSourceItem {
  children: GroupedItem[];
  level: number;
}

@Component({
  selector: 'list-connections',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    IconInduxtryComponent,
    ButtonComponent,
    IconActionButtonComponent,
  ],
  templateUrl: './list-connections.component.html',
  styleUrls: ['./list-connections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListConnections implements OnInit, OnDestroy {
  private _items = signal<DataSourceItem[]>([]);

  /** Ícones só para status em andamento (completed não exibe ícone na sidebar). */
  readonly connectionStatusIcon: Record<string, string> = {
    processing: STATUS_ICONS.processing,
    initializing: STATUS_ICONS.initializing,
    queued: STATUS_ICONS.queued,
  };

  @Input()
  set items(value: DataSourceItem[]) {
    this._items.set(value || []);
  }
  get items(): DataSourceItem[] {
    return this._items();
  }

  @Input()
  set collapsedConnections(value: boolean) {
    this._isCollapsedConnections.set(value ?? false);
  }
  get collapsedConnections(): boolean {
    return this._isCollapsedConnections();
  }

  /** ID da conexão selecionada pelo parent (ex.: após criar conexão) */
  @Input() selectedId = '';

  private _isSelected = signal<string>('');

  @Output() select = new EventEmitter<string>();
  @Output() create = new EventEmitter<void>();
  @Output() delete = new EventEmitter<string>();
  @Output() toggleCollapse = new EventEmitter<boolean>();
  @Output() refreshStatus = new EventEmitter<void>();

  private _isCollapsedConnections = signal(false);
  private statusPollingSubscription?: Subscription;
  addButton: ButtonInduxtry = {
    label: 'Adicionar',
    customStyle: 'iconButton',
    showLabel: false,
    key: 'add',
    icon: 'Plus',
  };

  deleteButton: ButtonInduxtry = {
    label: 'Deletar',
    customStyle: 'iconButton',
    showLabel: false,
    key: 'delete',
    icon: 'delete',
    customStyleFillIcon: true,
  };
  get isCollapsedConnections(): boolean {
    return this._isCollapsedConnections();
  }

  ngOnInit(): void {
    this.startStatusPolling();
  }

  ngOnDestroy(): void {
    this.stopStatusPolling();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && this.items.length > 0) {
      const lastSelectedSource =
        sessionStorage.getItem('lastSelectedSource') ??
        sessionStorage.getItem('lastSelectedDataset');
      if (lastSelectedSource) {
        this.onLoadSelectedItem(lastSelectedSource);
      }
    }
  }

  onItemClick(item: DataSourceItem): void {
    sessionStorage.setItem('lastSelectedSource', item._id);
    sessionStorage.removeItem('lastSelectedDataset');
    this._isSelected.set(item._id);
    this.select.emit(item._id);
  }

  onDeleteClick(item: DataSourceItem): void {
    this.delete.emit(item._id);
  }

  onCreateClick(): void {
    this.create.emit();
  }

  onToggleCollapse(): void {
    this._isCollapsedConnections.update(value => !value);
    this.toggleCollapse.emit(this._isCollapsedConnections());
  }

  private startStatusPolling(): void {
    this.stopStatusPolling();
    this.statusPollingSubscription = interval(300000).subscribe(() => {
      this.refreshStatus.emit();
    });
  }

  private stopStatusPolling(): void {
    if (this.statusPollingSubscription) {
      this.statusPollingSubscription.unsubscribe();
      this.statusPollingSubscription = undefined;
    }
  }

  getStatusClass(status: DataSourceStatus): string {
    return status === 'online' ? 'status-online' : 'status-offline';
  }

  get isSelectedItemId(): string {
    return this.selectedId || this._isSelected();
  }

  onLoadSelectedItem(id: string): void {
    this._isSelected.set(id);
    this.select.emit(id);
  }
}
